import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Tag } from "antd";

interface ExpandableTagListProps {
  items?: string[] | null;
  limit?: number;
  /** When set, ignores `limit` and instead shows as many tags as fit within this many
   * wrapped rows for the available cell width, measured via a hidden clone. */
  maxLines?: number;
  emptyPlaceholder?: string;
  tagColor?: string;
}

const toggleStyle: React.CSSProperties = {
  display: "block",
  padding: 0,
  marginTop: 4,
  marginLeft: 0,
  fontSize: 11,
  lineHeight: "14px",
  color: "var(--tertiary-color)",
  cursor: "pointer",
  userSelect: "none",
  width: "fit-content",
};

export const getVisibleTagCount = (offsetTops: number[], maxLines: number): number => {
  if (!offsetTops.length || maxLines < 1) return 0;

  let rows = 1;
  let rowTop = offsetTops[0];
  let count = 0;

  for (const offsetTop of offsetTops) {
    // A 1px tolerance avoids treating sub-pixel rounding as a new wrapped row.
    if (offsetTop > rowTop + 1) {
      rows += 1;
      rowTop = offsetTop;
      if (rows > maxLines) break;
    }
    count += 1;
  }

  return count;
};

export const ExpandableTagList: React.FC<ExpandableTagListProps> = ({
  items = [],
  limit = 5,
  maxLines,
  emptyPlaceholder = "-",
  tagColor,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [measuredLimit, setMeasuredLimit] = useState(limit);
  const measureRef = useRef<HTMLDivElement>(null);
  const tagRefs = useRef<(HTMLElement | null)[]>([]);
  const tagRefCallbacks = useRef<Record<number, (element: HTMLElement | null) => void>>({});

  const getTagRef = (index: number) => {
    if (!tagRefCallbacks.current[index]) {
      tagRefCallbacks.current[index] = (element) => {
        tagRefs.current[index] = element;
      };
    }
    return tagRefCallbacks.current[index];
  };

  const normalizedItems = useMemo(
    () => (Array.isArray(items) ? items : []).filter((item) => typeof item === "string" && item.trim().length > 0),
    [items]
  );

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [expanded]);

  useLayoutEffect(() => {
    if (!maxLines) return undefined;

    const recalcVisibleCount = () => {
      tagRefs.current.length = normalizedItems.length;
      const tags = tagRefs.current.filter((tag): tag is HTMLElement => Boolean(tag));
      setMeasuredLimit(getVisibleTagCount(tags.map((tag) => tag.offsetTop), maxLines));
    };

    recalcVisibleCount();

    const node = measureRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(recalcVisibleCount);
    observer.observe(node);
    return () => observer.disconnect();
  }, [maxLines, normalizedItems]);

  if (normalizedItems.length === 0) {
    return <>{emptyPlaceholder}</>;
  }

  const effectiveLimit = maxLines ? measuredLimit : limit;
  const hasMore = normalizedItems.length > effectiveLimit;
  const hiddenCount = hasMore ? normalizedItems.length - effectiveLimit : 0;
  const visibleItems = expanded || !hasMore ? normalizedItems : normalizedItems.slice(0, effectiveLimit);

  return (
    <div style={maxLines ? { position: "relative" } : undefined}>
      {maxLines && (
        <div
          aria-hidden
          ref={measureRef}
          style={{ position: "absolute", top: 0, left: 0, right: 0, visibility: "hidden", height: 0, overflow: "hidden" }}
        >
          {normalizedItems.map((item, index) => (
            <Tag
              key={`measure-${item}-${index}`}
              data-measure-index={index}
              ref={getTagRef(index)}
              style={{ marginBottom: "4px" }}
            >
              {item}
            </Tag>
          ))}
        </div>
      )}
      {visibleItems.map((item, index) => (
        <Tag key={`${item}-${index}`} color={tagColor} style={{ marginBottom: "4px" }}>
          {item}
        </Tag>
      ))}
      {hasMore && (
        <span
          role="button"
          tabIndex={0}
          style={toggleStyle}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setExpanded((current) => !current);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              setExpanded((current) => !current);
            }
          }}
        >
          {expanded ? "Show less" : `+${hiddenCount} more`} {expanded ? <UpOutlined /> : <DownOutlined />}
        </span>
      )}
    </div>
  );
};

